import styles from './GenericPayLinkLanding/GenericPayLinkLanding.module.css';
import { HelpTextAccordion } from './HelpTextAccordion/HelpTextAccordion';
import {
  submitBuyerStepZeroNewCustomer,
  submitBuyerStepZeroReturningCustomer,
} from './PayLater/CreateUserForm/submitBuyerStepZero';
import landingStyles from './PayLinkLanding.module.css';
import { PaymentDetails } from './PaymentDetails/PaymentDetails';
import { FormValues, PayLink } from './utils/PayLinkLanding.types';
import {
  getPricingPromotions,
  getRepresentativeInterest,
  shouldRedirectToCheckout,
} from './utils/productPricingHelpers';
import {
  SELLER_GET_ERROR,
  useGetPayLinkByPayLinkId,
  useGetSellerByHandle,
} from '../../api/lending/lapiHooks';
import { LoadingScreen } from '../../components/LoadingScreen/LoadingScreen';
import { ReactComponent as IwocaPayIcon } from '../../components/svg/IwocaPayIcon.svg';
import { GenericError } from '../../Pages/FailurePages/GenericError';
import { PayLinkNotFound } from '../../Pages/FailurePages/PayLinkNotFound';
import { postLendingApiJson } from '../../Pages/lendingApiFetch';
import { useGetValidFormalOffer } from '../../Seller/PayLinks/hooks/useGetValidFormalOffers';
import { FormalOffer } from '../../Seller/PayLinks/utils/Paylinks.types';
import { LendingApiError } from '../../utils/LendingApiError';
import { CancelButton } from '../components/CancelButton/CancelButton';
import { useEnabledOptions } from '../hooks/useEnabledTerms';
import { useTrackLandingPageView } from '../hooks/useTrackLandingPageView';
import { SIGNUP_BASE_PATH } from '../Signup/routes';
import { buildQueryString } from '../utils/queryParams';

export const PayLinkLanding = ({ payLinkId }: { payLinkId: string }) => {
  const { payLink, loadingPayLink, payLinkError } = useGetPayLinkByPayLinkId({
    payLinkId,
  });

  const { enabledOptions, loadingOptions } = useEnabledOptions({
    pricing: payLink?.product_pricing,
    allowedOptions: payLink?.allowed_payment_terms,
  });
  const { sellerError, loadingSeller } = useGetSellerByHandle(
    payLink?.seller_handle,
  );
  const { formalOffer, loading: loadingValidFormalOffer } =
    useGetValidFormalOffer();

  useTrackLandingPageView({
    payLinkType: 'custom',
    visibleOptions: enabledOptions,
  });

  if (sellerError) {
    return <GenericError errorText={"We can't find the seller"} />;
  }

  if (payLinkError) {
    return <PayLinkNotFound />;
  }

  if (
    loadingPayLink ||
    !payLink ||
    loadingSeller ||
    loadingOptions ||
    loadingValidFormalOffer
  ) {
    return <LoadingScreen />;
  }

  if (payLink.deleted_at) {
    return <GenericError errorText={SELLER_GET_ERROR} />;
  }

  const pricingPromotions = getPricingPromotions({
    pricing: payLink.pricing,
    product_pricing: payLink.product_pricing,
  });

  const representativeInterest = getRepresentativeInterest({
    pricing: payLink.pricing,
    product_pricing: payLink.product_pricing,
  });

  return (
    <div className={styles.page}>
      <div className={styles.container}>
        <div className={styles.header}>
          <h1>Your payment to {payLink.seller_name}</h1>
          <p className={styles.poweredBy}>
            Powered by&nbsp;
            <IwocaPayIcon className={landingStyles.iwocaPayLogo} />
          </p>
        </div>
        <div className={styles.contentContainer}>
          <div className={styles.paymentDetailsColumn}>
            {payLink.cancel_url && (
              <CancelButton className={styles.cancelButton} payLink={payLink} />
            )}
            <PaymentDetails
              sellerName={payLink.seller_name}
              pricingPromotions={pricingPromotions}
              representativeInterest={representativeInterest}
              onSubmit={onSubmit({ payLink, formalOffer })}
              initialValues={{
                amount: payLink.amount.toString(),
                reference: payLink.reference,
              }}
              enabledOptions={enabledOptions}
              payLinkType="custom"
            />
            <div className={styles.privacyPolicy}>
              <span>Find out how we use your data in our </span>
              <a
                href="/privacy-policy/"
                target="_blank"
                rel="noopener noreferrer"
              >
                Privacy Policy
              </a>
            </div>
          </div>
          <HelpTextAccordion
            className={styles.helpAccordion}
            enabledOptions={enabledOptions}
          />
        </div>
      </div>
    </div>
  );
};

const onSubmit =
  ({ payLink, formalOffer }: { payLink: PayLink; formalOffer?: FormalOffer }) =>
  ({ values, stateKey }: { values: FormValues; stateKey?: string }) => {
    const { paymentOption } = values;

    if (paymentOption === 'payNow') {
      return onSubmitPayNow(payLink.id);
    }
    return onSubmitPayLater({ values, payLink, stateKey, formalOffer });
  };

const PAY_NOW_URL = '/api/lending/edge/single_immediate_payment/';
const onSubmitPayNow = async (payLinkId: string) => {
  const response = await createPaymentForPayLink(payLinkId);

  const responseData = await response.json();
  if (!response.ok) {
    throw new LendingApiError(responseData, response.status);
  }

  redirectToPaymentProvider(responseData.data.payment_provider_auth_uri);
};

const createPaymentForPayLink = (payLinkId: string) => {
  return postLendingApiJson({
    url: PAY_NOW_URL,
    body: {
      data: {
        pay_link_id: payLinkId,
      },
    },
  });
};

const redirectToPaymentProvider = (redirectUrl: string) => {
  window.location.href = redirectUrl;
};

const onSubmitPayLater = async ({
  values,
  payLink,
  stateKey,
  formalOffer,
}: {
  values: FormValues;
  payLink: PayLink;
  stateKey?: string;
  formalOffer?: FormalOffer;
}) => {
  const queryString = buildQueryString({ payLinkId: payLink.id });

  if (stateKey) {
    await submitBuyerStepZeroReturningCustomer(
      stateKey,
      payLink,
      values.paymentOption!,
    );

    if (
      shouldRedirectToCheckout({
        paymentOption: values.paymentOption,
        payLinkAmount: parseFloat(values.amount),
        formalOffer,
      })
    ) {
      window.location.href = `/pay/checkout/offer/${queryString}`;
      return;
    }

    window.location.href = `/account/iwocapay/${queryString}`;
    return;
  }

  await submitBuyerStepZeroNewCustomer(
    values.emailAddress,
    values.marketingOptIn,
    payLink,
    values.paymentOption!,
  );

  window.location.href = `${SIGNUP_BASE_PATH}${queryString}`;
};
